import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  LayoutSingleColumn,
  NamedLink,
  NamedRedirect,
  CookiePolicy,
  ExternalLink,
  PrivacyPolicy,
} from '../../components';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { generateContentFromTranslationMessages } from '../../util/contentGenerator';
import { ensureCurrentUser } from '../../util/data';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { sendContactMessage } from '../ContactPage/ContactPage.duck';
import { help } from './help';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import StaticPage from '../PageBuilder/StaticPage';
import ContactForm from '../../forms/ContactForm/ContactForm';
import { useConfiguration } from '../../context/configurationContext';
import classNames from 'classnames';
import { LOCALE_CODE } from '../../constants/locales';

import css from './HelpCenterPage.module.css';

const getTranslationsWithValues = (currentUser, interfaceLang) => {
  const contactLink = (
    <NamedLink
      className={classNames({ [css.lowercase]: interfaceLang !== LOCALE_CODE.DE_DE })}
      key="contactPage"
      name="ContactPage"
    >
      <FormattedMessage id="General.contact" />
    </NamedLink>
  );

  return [
    {
      key: 'HelpCenter.the-activity.section0subsection2paragraph1',
      values: { contact: contactLink },
    },
    {
      key: 'HelpCenter.partners.section0subsection1item1',
      values: {
        link1: (
          <ExternalLink href="https://www.visitgenoa.it">https://www.visitgenoa.it</ExternalLink>
        ),
        link2: (
          <ExternalLink href="https://www.luganoregion.com">
            https://www.luganoregion.com
          </ExternalLink>
        ),
        link3: <ExternalLink href="https://www.ticino.ch">https://www.ticino.ch</ExternalLink>,
        link4: (
          <ExternalLink href="https://www.bellinzonaevalli.ch">
            https://www.bellinzonaevalli.ch
          </ExternalLink>
        ),
        link5: (
          <ExternalLink href="https://www.ascona-locarno.com">
            https://www.ascona-locarno.com
          </ExternalLink>
        ),
      },
    },
    {
      key: 'HelpCenter.partners.section0subsection1item2',
      values: {
        link1: <ExternalLink href="https://amsi.it">https://amsi.it</ExternalLink>,
        link2: (
          <ExternalLink href="https://www.giorgiorocca.com">
            https://www.giorgiorocca.com
          </ExternalLink>
        ),
        link3: (
          <ExternalLink href="https://milanocortina2026.olympics.com">
            https://milanocortina2026.olympics.com
          </ExternalLink>
        ),
        link4: <ExternalLink href="https://w3mountain.com">https://w3mountain.com</ExternalLink>,
      },
    },
    {
      key: 'HelpCenter.partners.section0subsection1item3',
      values: {
        link1: <ExternalLink href="https://getcarv.com">https://getcarv.com</ExternalLink>,
        link2: (
          <ExternalLink href="https://www.nidecker.com">https://www.nidecker.com</ExternalLink>
        ),
        link3: (
          <ExternalLink href="https://www.flow-bindings.com">
            https://www.flow-bindings.com
          </ExternalLink>
        ),
        link4: (
          <ExternalLink href="https://www.yoyo-tennis.ch">https://www.yoyo-tennis.ch</ExternalLink>
        ),
        link5: <ExternalLink href="https://www.toko.ch">https://www.toko.ch</ExternalLink>,
        link6: (
          <ExternalLink href="https://www.hellyhansen.com">
            https://www.hellyhansen.com
          </ExternalLink>
        ),
        link7: <ExternalLink href="https://www.zagskis.com">https://www.zagskis.com</ExternalLink>,
        link8: <ExternalLink href="https://elanskis.com">https://elanskis.com</ExternalLink>,
      },
    },
    {
      key: 'HelpCenter.account-security.section0subsection3item6',
      values: { contact: contactLink },
    },
    {
      key: 'HelpCenter.safety-concerns.section0paragraph3',
      values: { contact: contactLink },
    },
    {
      key: 'HelpCenter.sos-coverage-and-incidents.section0paragraph1',
      values: {
        link: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'terms-and-policies', subtab: 'insurance-policy' }}
            style={{ textTransform: 'lowercase' }}
          >
            <FormattedMessage id="General.insurancePolicy" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.terms-and-conditions.section0paragraph1',
      values: {
        termsAndConditions: (
          <NamedLink name="TermsAndConditionsPage">
            <FormattedMessage id="General.termsAndConditions" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.cookie-policy.section0paragraph1',
      values: {
        cookiePolicy: <CookiePolicy isWidget />,
      },
    },
    {
      key: 'HelpCenter.privacy-policy.section0paragraph1',
      values: {
        privacyPolicy: <PrivacyPolicy isWidget />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item1',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item2',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item3',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item4',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item5',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item6',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item7',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item8',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item9',
      values: {
        lineBreak: <br />,
        link: (
          <ExternalLink href="https://www.erv.ch/protection-des-donnees">
            www.erv.ch/protection-des-donnees
          </ExternalLink>
        ),
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item10',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item11',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item15',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item16',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.insurance-policy.section0subsection1item21',
      values: {
        lineBreak: <br />,
      },
    },
    {
      key: 'HelpCenter.payments-terms.section0paragraph1',
      values: {
        stripe: (
          <ExternalLink href="https://stripe.com/it-ch/legal/connect-account">
            <FormattedMessage id="General.stripeConnectedAccountTermsLink" />
          </ExternalLink>
        ),
      },
    },
    {
      key: 'HelpCenter.what-can-i-find.section0paragraph1',
      values: {
        home: (
          <NamedLink name="LandingPage">
            <FormattedMessage id="HelpCenter.homePage" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.booking-an-activity.section0subsection2paragraph1',
      values: {
        article: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'your-bookings', subtab: 'booking-status' }}
          >
            <FormattedMessage id="HelpCenter.article" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.changes-and-cancellations.section0paragraph1',
      values: {
        conditions: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'terms-and-policies', subtab: 'cancellation-policy' }}
          >
            <FormattedMessage id="HelpCenter.conditions" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.preparing-for-an-activity.section0paragraph1',
      values: {
        safetyArticle: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'safety', subtab: 'safety-tips-for-travelers' }}
          >
            <FormattedMessage id="HelpCenter.preparing-for-an-activity.safetyTips" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.issue-with-a-booking.section0paragraph1',
      values: {
        contact: (
          <NamedLink key="contactPage" name="ContactPage">
            <FormattedMessage id="General.contact" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.refunds.section0paragraph1',
      values: {
        cancellationPolicy: (
          <NamedLink
            className={css.lowercase}
            name="HelpCenterPage"
            params={{ tab: 'terms-and-policies', subtab: 'cancellation-policy' }}
          >
            <FormattedMessage id="General.cancellationPolicy" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.id-and-verification.section0paragraph1',
      values: {
        stripe: (
          <ExternalLink href="https://stripe.com/en-ch">
            <FormattedMessage id="General.stripe" />
          </ExternalLink>
        ),
      },
    },
    {
      key: 'HelpCenter.requirements.section0subsection2item7',
      values: {
        cancellationPolicy: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'terms-and-policies', subtab: 'cancellation-policy' }}
          >
            <FormattedMessage id="General.cancellationPolicy" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.requirements.section0subsection2item8',
      values: {
        ratingsAndReviews: (
          <NamedLink name="HelpCenterPage" params={{ tab: 'your-account', subtab: 'reviews' }}>
            <FormattedMessage id="HelpCenter.ratingsAndReviews" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.the-listing.section0paragraph1',
      values: {
        standardsAndRequirements: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'about-offering-a-service', subtab: 'requirements' }}
          >
            <FormattedMessage id="HelpCenter.standardsAndRequirements" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.the-listing.section0subsection2paragraph1',
      values: {
        article: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'managing-your-listings', subtab: 'create-and-edit-your-listing' }}
          >
            <FormattedMessage id="HelpCenter.article" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.create-and-edit-your-listing.section0subsection1item1',
      values: {
        activityTypesHere: (
          <NamedLink name="HelpCenterPage" params={{ tab: 'about-crion', subtab: 'the-activity' }}>
            <FormattedMessage id="HelpCenter.activityTypesHere" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.create-and-edit-your-listing.section0subsection1item7',
      values: {
        hereTheActivityRequirements: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'about-offering-a-service', subtab: 'requirements' }}
          >
            <FormattedMessage id="HelpCenter.hereTheActivityRequirements" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.create-and-edit-your-listing.section0subsection2paragraph1',
      values: {
        publicProfilePage: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'your-account', subtab: 'completing-your-profile' }}
          >
            <FormattedMessage id="HelpCenter.publicProfilePage" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.publish-your-listing.section0paragraph1',
      values: {
        verificationProcess: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'your-account', subtab: 'id-and-verification' }}
          >
            <FormattedMessage id="HelpCenter.verificationProcess" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.publish-your-listing.section0subsection1paragraph1',
      values: {
        cancellations: (
          <NamedLink
            className={classNames({ [css.lowercase]: interfaceLang !== LOCALE_CODE.DE_DE })}
            name="HelpCenterPage"
            params={{ tab: 'your-reservations', subtab: 'cancellations' }}
          >
            <FormattedMessage id="HelpCenter.cancellations" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.calendar-and-reservations.section0subsection1paragraph3',
      values: {
        cancellations: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'your-reservations', subtab: 'cancellations' }}
          >
            <FormattedMessage id="General.cancellation" />
          </NamedLink>
        ),
        hereHowYouCanEdit: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'managing-your-listings', subtab: 'create-and-edit-your-listing' }}
          >
            <FormattedMessage id="HelpCenter.hereHowYouCanEdit" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.pricing-and-fees-2.section0subsection2paragraph1',
      values: {
        yourListingDescription: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'managing-your-listings', subtab: 'create-and-edit-your-listing' }}
          >
            <FormattedMessage id="HelpCenter.pricing-and-fees-2.yourListingDescription" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.cancellations.section0subsection4paragraph2',
      values: {
        cancellationPolicy: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'terms-and-policies', subtab: 'cancellation-policy' }}
          >
            <FormattedMessage id="General.cancellationPolicy" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.communicating-with-travelers.section0subsection2item4',
      values: {
        safetyTips: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'safety', subtab: 'safety-tips-for-providers' }}
          >
            <FormattedMessage id="HelpCenter.safetyTips" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.issue-with-a-reservation.section0paragraph1',
      values: {
        contact: (
          <NamedLink key="contactPage" name="ContactPage">
            <FormattedMessage id="General.contact" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.payout.section0paragraph2',
      values: {
        addABankAccount: (
          <NamedLink
            name="HelpCenterPage"
            params={{ tab: 'your-account', subtab: 'managing-your-account' }}
          >
            <FormattedMessage id="HelpCenter.addABankAccount" />
          </NamedLink>
        ),
      },
    },
    {
      key: 'HelpCenter.payout.section0paragraph5',
      values: {
        cancellation: (
          <NamedLink
            className={classNames({ [css.lowercase]: interfaceLang !== LOCALE_CODE.DE_DE })}
            name="HelpCenterPage"
            params={{ tab: 'your-reservations', subtab: 'cancellations' }}
          >
            <FormattedMessage id="General.cancellation" />
          </NamedLink>
        ),
      },
    },
  ];
};

const HelpCenterPage = props => {
  const {
    params,
    intl,
    // Contact
    currentUser,
    sendContactMessageInProgress,
    sendContactMessageError,
    sendContactMessageDone,
    onMessageSubmit,
    interfaceLang,
  } = props;

  const config = useConfiguration();

  const { tab, subtab } = params;

  const ensuredCurrentUser = useMemo(() => ensureCurrentUser(currentUser), [currentUser]);
  const { email, profile } = ensuredCurrentUser.attributes || {};
  const { firstName, lastName } = profile || {};

  const tabs = useMemo(
    () => help.find(h => h.subsections.find(hs => hs.subtitleKey === tab))?.subsections,
    [tab]
  );

  const content = useMemo(
    () =>
      generateContentFromTranslationMessages(
        `HelpCenter.${subtab}`,
        intl,
        getTranslationsWithValues(currentUser, interfaceLang)
      ),
    [currentUser, interfaceLang, intl, subtab]
  );

  const contactMaybe = useMemo(
    () =>
      subtab === 'contact' && (
        <ContactForm
          className={css.form}
          initialValues={{ firstName, lastName, email }}
          onSubmit={onMessageSubmit}
          inProgress={sendContactMessageInProgress}
          ready={sendContactMessageDone}
          error={sendContactMessageError}
        />
      ),
    [
      email,
      firstName,
      lastName,
      onMessageSubmit,
      sendContactMessageDone,
      sendContactMessageError,
      sendContactMessageInProgress,
      subtab,
    ]
  );

  const messages = useMemo(
    () => ({
      title: intl.formatMessage({ id: 'General.help' }),
      tab: intl.formatMessage({ id: `HelpCenter.${tab}` }),
      subtab: intl.formatMessage({ id: `HelpCenter.${subtab}` }),
    }),
    [intl, subtab, tab]
  );

  const title = `${messages.title} / ${messages.tab} / ${messages.subtab}`;

  const helpAvailable = useMemo(
    () => tabs && tabs.find(t => t.links.find(l => l.textKey === subtab)),
    [subtab, tabs]
  );

  if (!helpAvailable) {
    return <NamedRedirect name="HelpCenterBasePage" />;
  }

  return (
    <StaticPage
      title={title}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'HelpCenterPage',
        name: messages.title,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="HelpCenterPage" />}
        footer={<FooterContainer />}
        mainColumnClassName={css.staticPageWrapper}
      >
        <h3>{title}</h3>
        <div className={css.main}>
          <aside>
            {tabs.map((t, i) => (
              <React.Fragment key={`${t.subtitleKey}_${i}`}>
                <NamedLink
                  key={t.subtitleKey}
                  className={css.parentLink}
                  name="HelpCenterPage"
                  params={t.links[0].params}
                >
                  <FormattedMessage id={`HelpCenter.${t.subtitleKey}`} />
                </NamedLink>
                {t.subtitleKey === tab &&
                  t.links.map(link => {
                    const { href, parentTextKey = 'HelpCenter', textKey, params } = link;

                    return href ? (
                      <ExternalLink key={textKey} href={href} className={css.link}>
                        <FormattedMessage id={`${parentTextKey}.${textKey}`} />
                      </ExternalLink>
                    ) : (
                      <NamedLink
                        key={textKey}
                        className={css.link}
                        name="HelpCenterPage"
                        params={params}
                      >
                        <FormattedMessage id={`${parentTextKey}.${textKey}`} />
                      </NamedLink>
                    );
                  })}
              </React.Fragment>
            ))}
          </aside>
          <section className={css.content}>
            <h3>{messages.subtab}</h3>
            {content}
            {contactMaybe}
            <NamedLink name="LandingPage" className={css.backToCrion}>
              <FormattedMessage
                id="HelpCenter.backToCrion"
                values={{ marketplaceName: config.marketplaceName }}
              />
            </NamedLink>
          </section>
        </div>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { interfaceLang } = state.ui;
  const {
    sendContactMessageInProgress,
    sendContactMessageError,
    sendContactMessageDone,
  } = state.ContactPage;

  return {
    currentUser,
    sendContactMessageInProgress,
    sendContactMessageError,
    sendContactMessageDone,
    scrollingDisabled: isScrollingDisabled(state),
    interfaceLang,
  };
};

const mapDispatchToProps = dispatch => ({
  onMessageSubmit: values => dispatch(sendContactMessage(values)),
});

export default compose(
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(HelpCenterPage);
